import {
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  Typography,
  makeStyles,
  ListItemText,
  Checkbox,
} from '@material-ui/core';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../common/Icon';
import clsx from 'clsx';
import Input from '../../common/Input';
import { useStores } from '../../../stores';
import {
  contactReasons,
  zendeskAssigneeGroup,
  zendeskAssigneeGroupTest,
  zendeskCustomFields,
  zendeskCustomFieldsTest,
  zendeskDeaChannel,
  zendeskDeaChannelTest,
  zendeskRecipient,
  zendeskSubject,
  zendeskTag,
} from '../../../constants';
import SuccessStep from './SuccessStep';
import Stepper from './Stepper';
import AttachmentStep from './AttachmentStep';
import { ValidatorForm } from 'react-material-ui-form-validator';
import { countryCodes } from '../../../../../stores/Constants';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: '20px',
    width: '100%',
  },
  section: {
    width: '100%',
  },
  label: {
    margin: '20px 0 0',
    alignItems: 'flex-start',
    '& .MuiFormControlLabel-label': {
      marginLeft: 0,
      paddingLeft: '10px',
    },
  },
  button: {
    minWidth: 90,
    minHeight: 48,
    borderRadius: '5px',
    [theme.breakpoints.down('sm')]: {
      minWidth: 90,
      minHeight: 48,
      borderRadius: '5px',
    },
    '& > span': {
      marginTop: 5,
      gap: '10px',
    },
  },
  closeIcon: {
    height: 24,
    width: 24,
    cursor: 'pointer',
  },
  reasonDescription: {
    fontSize: '11px',
  },
  inputLabel: {
    fontSize: '12px',
  },
  menuItem: {
    display: 'flex',
    flexDirection: 'row',
    gap: 10,
    alignItems: 'center',
  },
  filled: {
    border: `2px solid ${theme.customPalette.neutralGr03}`,
  },
  underline: {
    '&&&:before': {
      borderBottom: 'none',
    },
    '&&:after': {
      borderBottom: 'none',
    },
  },
  disabled: {
    backgroundColor: 'rgba(0, 0, 0, 0.13)',
  },
}));

const MissingPackageForm = ({
  setMissingPackageOpen,
  setMissingPackageFilled,
  ship,
  details,
  order,
}) => {
  const { t } = useTranslation('components');
  const classes = useStyles();
  const { uiStore, userStore } = useStores();
  const { user } = userStore;
  const [data, setData] = useState({
    reason: null,
    issues: [],
    comment: '',
    attachment: null,
    step: 1,
    numPackage: ship.sequence,
    ref: ship.ref,
    files: [],
    product: order?.short,
  });
  const [isLoading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const combinedOptions = [
    ...(details?.issues || [])
      .filter((item) => item?.sequence != null)
      .map((item) => ({
        ...item,
        label: item.name,
        type: 'ISSUE',
        sequence: `issue-${item.sequence}`,
      })),
    ...(details?.gifts || [])
      .filter((item) => item?.sequence != null)
      .map((item) => ({
        ...item,
        label: item.name,
        type: 'GIFT',
        sequence: `gift-${item.sequence}`,
      })),
  ];

  const filteredOptions = combinedOptions.filter(
    (item) =>
      item.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.sequence.replace(/^(issue|gift)-/, '').includes(searchQuery),
  );
  const handleOnChange = (comment) => {
    if (comment !== null) {
      setData((prev) => ({ ...prev, comment }));
    }
  };

  const getIssuesNames = (issues) => {
    if (!issues || issues.length === 0) {
      return t('components:MissingPackage.damageStep.noIssue');
    }

    const issuesList = issues
      .filter((issue) => issue && issue.startsWith('issue-'))
      .map((issue) => {
        const match = combinedOptions.find((item) => item.sequence === issue);
        return match ? match.label : '';
      })
      .filter(Boolean)
      .join(', ');

    const giftsList = issues
      .filter((issue) => issue && issue.startsWith('gift-'))
      .map((issue) => {
        const match = combinedOptions.find((item) => item.sequence === issue);
        return match ? match.label : '';
      })
      .filter(Boolean)
      .join(', ');

    let result = '';
    if (issuesList) result += `Issues: ${issuesList}`;
    if (giftsList) result += (result ? '\n' : '') + `Gifts: ${giftsList}`;
    return result || t('components:MissingPackage.damageStep.noIssue');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isLoading) return;

    setLoading(true);

    const description = `Shipping order: ${data?.ref}\n${getIssuesNames(
      data?.issues,
    )}\nReason: ${t(
      `components:MissingPackage.damageStep.${
        contactReasons[data?.reason]?.value
      }`,
    )}\nComment: ${data?.comment || ''}`;

    const orderLang = ship.order.substring(0, 2);
    const fallbackKey = countryCodes.uk?.toUpperCase();
    const zendeskRecipientValue =
      zendeskRecipient[orderLang] || zendeskRecipient[fallbackKey];
    let formParams = {
      ticket: {
        subject: zendeskSubject,
        description,
        comment: {
          body: description,
          uploads: data?.attachment,
        },
        group_id:
          process.env.REACT_APP_ENV === 'production'
            ? zendeskAssigneeGroup[orderLang] ||
              zendeskAssigneeGroup[fallbackKey]
            : zendeskAssigneeGroupTest[orderLang] ||
              zendeskAssigneeGroupTest[fallbackKey],
        recipient: zendeskRecipientValue[process.env.REACT_APP_BRAND],
        requester: {
          name: `${user.name} ${user.lastName}`,
          email: user.email,
        },
        tags: [ship.order, ship.ref, zendeskTag],
        custom_fields: [
          {
            id:
              process.env.REACT_APP_ENV === 'production'
                ? zendeskCustomFields.brand.id
                : zendeskCustomFieldsTest.brand.id,
            value:
              process.env.REACT_APP_BRAND === 'PW' ? 'partwork' : 'fanhome',
          },
          {
            id:
              process.env.REACT_APP_ENV === 'production'
                ? zendeskCustomFields.country.id
                : zendeskCustomFieldsTest.country.id,
            value: orderLang,
          },
          {
            id:
              process.env.REACT_APP_ENV === 'production'
                ? zendeskCustomFields.contactReason.id
                : zendeskCustomFieldsTest.contactReason.id,
            value: contactReasons[data?.reason].zd,
          },
          {
            id:
              process.env.REACT_APP_ENV === 'production'
                ? zendeskCustomFields.deaChannel.id
                : zendeskCustomFieldsTest.deaChannel.id,
            value:
              process.env.REACT_APP_ENV === 'production'
                ? zendeskDeaChannel
                : zendeskDeaChannelTest,
          },
        ],
      },
    };

    try {
      await fetch(`${process.env.REACT_APP_ZENDESK_API_TICKET_URL}/tickets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formParams),
      })
        .then(() => {
          setData((prev) => ({ ...prev, step: prev.step + 1 }));
          setMissingPackageFilled(true);
        })
        .catch(() => uiStore.showGenericError());
    } catch {
      uiStore.showGenericError();
    } finally {
      setLoading(false);
    }
  };

  const showIntro = () => {
    return (
      <Grid item id="intro">
        <Box
          display="flex"
          flexDirection="row"
          spacing={2}
          alignItems="flex-start"
          justifyContent="space-between">
          <Typography variant="subtitle2">
            {t('components:MissingPackage.mainTitle', {
              numPackage: t('components:Package.packageNumber', {
                number: ship.sequence,
              }),
            })}
          </Typography>
          <Icon
            className={classes.closeIcon}
            name="close"
            onClick={() => {
              setMissingPackageOpen(false);
            }}
          />
        </Box>
        <Box className="mt-2">
          <Typography variant="body2">
            {t('components:MissingPackage.description')}
          </Typography>
        </Box>
      </Grid>
    );
  };

  return (
    <div className={classes.container}>
      <ValidatorForm id="missingDamaged" onSubmit={handleSubmit}>
        <Grid container direction="column" spacing={6}>
          {data.step === 1 && (
            <>
              {showIntro()}
              <Grid item id="reason" className={classes.section}>
                <div>
                  {t('components:MissingPackage.damageStep.titleSection1')}
                </div>
                <div>
                  <FormControl component="fieldset" fullWidth>
                    <RadioGroup
                      aria-label="reason"
                      name="reason"
                      value={data?.reason}
                      onChange={(e) => {
                        setData((prev) => ({
                          ...prev,
                          reason: e.target.value,
                        }));
                      }}>
                      <Grid container>
                        {Object.keys(contactReasons).map((it) => (
                          <Grid item xs={12} md={6}>
                            <FormControlLabel
                              className={classes.label}
                              value={contactReasons[it].value}
                              control={<Radio />}
                              label={
                                <>
                                  <div>{contactReasons[it].label}</div>
                                  <div>
                                    {t(
                                      `components:MissingPackage.damageStep.${contactReasons[it].value}`,
                                    )}
                                  </div>
                                  {contactReasons[it].value ===
                                    contactReasons.missingParcel.value && (
                                    <div className={classes.reasonDescription}>
                                      {t(
                                        'components:MissingPackage.damageStep.missingParcelDescription',
                                        {
                                          number:
                                            process.env
                                              .REACT_APP_PACKAGE_MISSING_DAYS,
                                        },
                                      )}
                                    </div>
                                  )}
                                </>
                              }
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </RadioGroup>
                  </FormControl>
                </div>
              </Grid>
              <Grid item id="issue" className={classes.section}>
                <div>
                  {t('components:MissingPackage.damageStep.titleSection2')}
                </div>
                <div>
                  <FormControl fullWidth>
                    <InputLabel htmlFor="issue" className={classes.inputLabel}>
                      {t('components:MissingPackage.damageStep.placeholder1')}
                    </InputLabel>
                    <Select
                      labelId="issue"
                      label="issue"
                      variant="filled"
                      className={clsx(
                        classes.filled,
                        classes.underline,
                        (!data?.reason ||
                          data?.reason ===
                            contactReasons.missingParcel.value) &&
                          classes.disabled,
                      )}
                      multiple
                      fullWidth
                      disabled={
                        !data?.reason ||
                        data?.reason === contactReasons.missingParcel.value
                      }
                      value={data?.issues}
                      onChange={(e) =>
                        setData((prev) => ({ ...prev, issues: e.target.value }))
                      }
                      onClose={() => setSearchQuery('')}
                      renderValue={(selected) =>
                        combinedOptions
                          .filter((item) => selected.includes(item.sequence))
                          .map((item) => item.label)
                          .join(', ')
                      }>
                      <Box px={2}>
                        <Input
                          label={t(
                            'components:MissingPackage.damageStep.placeholderSearchIssue',
                          )}
                          fullWidth
                          value={searchQuery}
                          onChange={handleSearchChange}
                        />
                      </Box>
                      {data?.reason &&
                        data?.reason !== contactReasons.missingParcel.value &&
                        filteredOptions.map((item) => (
                          <MenuItem
                            className={classes.menuItem}
                            key={item.sequence}
                            value={item.sequence}>
                            <Checkbox
                              checked={
                                (data?.issues).indexOf(item?.sequence) > -1
                              }
                            />
                            <ListItemText
                              primary={`#${item.sequence.replace(
                                /^(issue|gift)-/,
                                '',
                              )}. ${item.name}`}
                            />
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </div>
              </Grid>
              <Grid item id="comment" className={classes.section}>
                <div>
                  {t('components:MissingPackage.damageStep.titleSection3')}
                </div>
                <div>
                  <Input
                    fullWidth
                    value={data?.comment}
                    onChange={(e) => handleOnChange(e.target.value)}
                    label={t(
                      'components:MissingPackage.damageStep.placeholder2',
                    )}
                  />
                </div>
              </Grid>
            </>
          )}
          {data.step === 2 && (
            <>
              {showIntro()}
              <AttachmentStep setData={setData} />
            </>
          )}
          {data?.step === 3 ? (
            <SuccessStep
              setMissingPackageOpen={setMissingPackageOpen}
              data={data}
            />
          ) : (
            <Stepper data={data} setData={setData} />
          )}
        </Grid>
      </ValidatorForm>
    </div>
  );
};

export default MissingPackageForm;
